import { Component, OnInit, ViewChild } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { Features } from '@app/core/user-permissions/user-permissions.model';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import { MenuTaskAssignComponent } from '@app/shared/components/menu-task-assign/menu-task-assign.component';
import { PaginationMeta, Reservation, Task, User } from '@app/shared/interfaces';
import { DashboardService } from '@app/shared/services/dashboard/dashboard.service';
import { ReservationService } from '@app/shared/services/reservation/reservation.service';
import { TasksService } from '@app/shared/services/tasks/tasks.service';
import { addSeconds } from 'date-fns';
import { DashboardDialogComponent } from '../../components/dashboard-dialog/dashboard-dialog.component';

@Component({
  standalone: false,
  selector: 'sbnb-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit {
  Features = Features;

  user: User;
  greetingToD: string;
  selectedTask: Task;
  loading: boolean;
  counts$ = this.dashboardService.getDashboardCounts();

  checkins: Reservation[];
  checkinsLoading: boolean;
  checkinsPagination: PaginationMeta;

  checkouts: Reservation[];
  checkoutsLoading: boolean;
  checkoutsPagination: PaginationMeta;

  tasks: Task[];
  tasksLoading: boolean;
  tasksPagination: PaginationMeta;

  canViewTasks = false;
  canEditYourTasks = false;
  canEditAllTasks = false;
  hasInboxPermissions: boolean;

  @ViewChild('menuTaskAssign') menuTaskAssign: MenuTaskAssignComponent;

  perms$ = this.userPermissionsService;

  constructor(
    private readonly authService: AuthenticationService,
    private readonly userPermissionsService: UserPermissionsService,
    private readonly router: Router,
    private readonly dashboardService: DashboardService,
    private readonly reservationService: ReservationService,
    private readonly taskService: TasksService,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit() {
    this.user = this.authService.getUserDetails();

    this.userPermissionsService.hasAccessTo(Features.PERM_TASKS).subscribe((res) => {
      this.canViewTasks = res;
    });

    this.userPermissionsService.hasAccessTo(Features.PERM_TASKS_UPDATE_YOURS).subscribe((res) => {
      this.canEditYourTasks = res;
    });

    this.userPermissionsService.hasAccessTo(Features.PERM_TASKS_UPDATE_ALL).subscribe((res) => {
      this.canEditAllTasks = res;
    });

    this.userPermissionsService.hasAccessTo(Features.PERM_INBOX).subscribe((res) => {
      this.hasInboxPermissions = res;
    });

    const hours = new Date().getHours();
    this.greetingToD = hours < 12 ? 'morning' : hours < 18 ? 'afternoon' : 'evening';

    this.loadCheckins();
    this.loadCheckouts();
    this.loadTasks();
  }

  // The users browser date may be different to the date of the account, so we need to ensure we're loading from the correct date
  convertDateToUsersDate(date: Date): Date {
    const tzOffsetSeconds = this.user.timezone_offset ? this.user.timezone_offset : 0;
    const browserOffsetSeconds = date.getTimezoneOffset() * 60 * -1; // multiply by -1 to flip pos/neg

    const secondsToAdd = tzOffsetSeconds - browserOffsetSeconds;

    const usersDate = addSeconds(date, secondsToAdd);

    return usersDate;
  }

  loadCheckins(offset = 0, cursor?: string) {
    this.checkinsLoading = true;

    const usersDate = this.convertDateToUsersDate(new Date());

    this.reservationService.getCheckIns({ startDate: usersDate, offset, cursor }).subscribe((res) => {
      this.checkins = res.data;
      this.checkinsPagination = res.meta?.pagination;
      this.checkinsLoading = false;
    });
  }

  loadCheckouts(offset = 0, cursor?: string) {
    this.checkoutsLoading = true;

    const usersDate = this.convertDateToUsersDate(new Date());

    this.reservationService.getCheckOuts({ startDate: usersDate, offset, cursor }).subscribe((res) => {
      this.checkouts = res.data;
      this.checkoutsPagination = res.meta?.pagination;
      this.checkoutsLoading = false;
    });
  }

  loadTasks(offset = 0, cursor?: string) {
    this.tasksLoading = true;

    this.taskService.getUpcomingTasks({ offset, cursor }).subscribe((res) => {
      this.tasks = res.data;

      for (let i = 0; i < this.tasks.length; i++) {
        if (this.canEditAllTasks && this.tasks.length > 0) {
          this.tasks[i].computed.can_edit = true;
        } else if (this.canViewTasks || this.canEditYourTasks) {
          if (this.tasks[i]?.teammate?.email == this.user?.email) {
            this.tasks[i].computed.can_edit = true;
          } else {
            this.tasks.splice(i, 1);
          }
        }
      }
      this.tasksPagination = res.meta?.pagination;
      this.tasksLoading = false;
    });
  }

  removeTask(uuid: string) {
    const index = this.tasks.findIndex((x) => x.uuid === uuid);

    if (index > -1) {
      this.tasks.splice(index, 1);
    }
  }

  public handlePageChanged(newPageNo: number, type) {
    let newOffset;

    switch (type) {
      case 'checkins':
        newOffset = newPageNo * this.checkinsPagination.per_page - this.checkinsPagination.per_page;
        this.loadCheckins(newOffset);
        break;

      case 'checkouts':
        newOffset = newPageNo * this.checkoutsPagination.per_page - this.checkoutsPagination.per_page;
        this.loadCheckouts(newOffset);
        break;

      case 'tasks':
        newOffset = newPageNo * this.tasksPagination.per_page - this.tasksPagination.per_page;
        this.loadTasks(newOffset);
        break;

      default:
        break;
    }
  }

  handleCursorChanged(cursor: string, type: string) {
    switch (type) {
      case 'checkins':
        this.loadCheckins(null, cursor);
        break;

      case 'checkouts':
        this.loadCheckouts(null, cursor);
        break;

      case 'tasks':
        this.loadTasks(null, cursor);
        break;

      default:
        break;
    }
  }

  taskAssigned(newTaskData) {
    if (newTaskData.name) {
      const index = this.tasks.findIndex((x) => x.name === newTaskData.name);

      if (index > -1) {
        this.tasks[index] = newTaskData;
      }
    }
  }

  openDetailsModal(threadUuid: string) {
    // if mobile - just link through to the conversation
    if (window.innerWidth < 959 && this.hasInboxPermissions) {
      this.router.navigate(['/inbox/thread/' + threadUuid]);
      return;
    }

    this.dialog.open(DashboardDialogComponent, {
      width: '1000px',
      maxHeight: '90vh',
      minHeight: '500px',
      panelClass: 'dialog__no-padding',
      data: {
        threadUuid: threadUuid,
      },
    });
  }
  checkListToggleDashboard() {
    this.menuTaskAssign.taskChecklistHidden = false;
  }
}
